import { AIChatModeConfig } from '@/hooks/useAIChatModeConfig';
import { ChatTheme, Question, TriggerRule } from './theme';
import { ValidationSettings } from './settings';

// Unified settings schema combining all builder settings
export interface UnifiedChatbotSettings {
  // General Settings
  chatMode: 'question_flow' | 'ai_only';
  messageDelay: number;
  buttonOptionsDelay: number;
  noResponseDelay: number;
  optionsHandlerDelay: number;
  showTypingIndicator: boolean;
  showTimestamp: boolean;
  
  // AI Settings (consolidated from ai_settings)
  ai_chat_config: AIChatModeConfig;
  ai_provider: {
    provider: 'openai' | 'anthropic' | 'gemini';
    apiKey: string;
    model: string;
    temperature: number;
    maxTokens: number;
  };
  
  // Validation Settings
  validation_settings: ValidationSettings;
  
  // Company Information
  companyName: string;
  companyLogo: string;
  tcpaLanguage: string;
  
  // Custom Code
  custom_javascript: string;
  custom_html: string;
  
  // Operator Settings
  operatorName: string;
  operatorAvatarUrl: string;
}

// Default settings for new chatbots
export const DEFAULT_UNIFIED_SETTINGS: UnifiedChatbotSettings = {
  chatMode: 'question_flow',
  messageDelay: 1000,
  buttonOptionsDelay: 0,
  noResponseDelay: 2000,
  optionsHandlerDelay: 10,
  showTypingIndicator: true,
  showTimestamp: true,
  
  ai_chat_config: {
    aiPersonality: {
      name: 'AI Assistant',
      role: 'Customer Service Representative',
      description: 'A helpful and knowledgeable AI assistant focused on providing excellent customer service and support.'
    },
    conversationStyle: {
      tone: 'professional',
      responseLength: 'adaptive',
      proactiveness: 'balanced'
    },
    systemInstructions: `You are a helpful AI assistant representing this company. Your goal is to:

1. Provide accurate and helpful information
2. Maintain a professional and friendly tone
3. Ask clarifying questions when needed
4. Guide users toward solutions
5. Escalate to human support when appropriate

Always be respectful, patient, and focused on helping the user achieve their goals.`,
    conversationGuidelines: [
      'Always greet users warmly and professionally',
      'Ask clarifying questions to better understand user needs',
      'Keep responses conversational and under 150 characters when possible',
      'Use short, friendly messages that feel natural and engaging'
    ],
    prohibitedTopics: [
      'Personal financial advice',
      'Medical or health advice',
      'Legal advice',
      'Political opinions'
    ],
    collectUserInfo: true,
    requiredFields: [
      { name: 'name', description: 'I need to know your name' },
      { name: 'email', description: 'I need your email address to contact you' }
    ],
    optionalFields: [
      { name: 'company', description: 'What company do you work for?' },
      { name: 'phone', description: 'Your phone number for follow-up calls' }
    ],
    welcomeMessage: 'Hello! I\'m here to help you with any questions or concerns you may have. How can I assist you today?',
    fallbackMessage: 'I apologize, but I didn\'t quite understand that. Could you please rephrase your question or provide more details?',
    endConversationTriggers: ['goodbye', 'bye', 'end chat', 'no thanks', 'that\'s all'],
    
    // Multi-Offer Configuration
    offers: {
      enabled: false,
      categories: [
        {
          id: 'insurance',
          name: 'Insurance',
          description: 'Life, health, auto, and home insurance products',
          keywords: ['insurance', 'coverage', 'policy', 'premium', 'deductible', 'claim'],
          requiredFields: [
            { name: 'name', description: 'Your full name' },
            { name: 'email', description: 'Your email address' },
            { name: 'phone', description: 'Your phone number' }
          ],
          optionalFields: [
            { name: 'age', description: 'Your age for insurance quotes' },
            { name: 'current_coverage', description: 'Do you have existing insurance?' }
          ],
          customInstructions: 'Focus on understanding their insurance needs and provide relevant information about coverage options.'
        },
        {
          id: 'travel',
          name: 'Travel & Cruises',
          description: 'Travel packages, cruises, and vacation deals',
          keywords: ['travel', 'cruise', 'vacation', 'trip', 'destination', 'booking'],
          requiredFields: [
            { name: 'name', description: 'Your full name' },
            { name: 'email', description: 'Your email address' }
          ],
          optionalFields: [
            { name: 'travel_dates', description: 'When are you planning to travel?' },
            { name: 'destination', description: 'Where would you like to go?' },
            { name: 'budget', description: 'What\'s your approximate budget?' }
          ],
          customInstructions: 'Help them explore travel options and find the perfect vacation package.'
        },
        {
          id: 'mortgage',
          name: 'Mortgage Relief',
          description: 'Mortgage assistance, refinancing, and relief programs',
          keywords: ['mortgage', 'refinance', 'loan', 'payment', 'relief', 'foreclosure'],
          requiredFields: [
            { name: 'name', description: 'Your full name' },
            { name: 'email', description: 'Your email address' },
            { name: 'phone', description: 'Your phone number' }
          ],
          optionalFields: [
            { name: 'current_payment', description: 'Your current monthly payment' },
            { name: 'loan_balance', description: 'Remaining loan balance' },
            { name: 'property_value', description: 'Estimated property value' }
          ],
          customInstructions: 'Understand their mortgage situation and guide them toward appropriate relief options.'
        }
      ],

    },
    
    contextMemory: true,
    maxConversationLength: 50,
    escalationTriggers: ['speak to human', 'human agent', 'manager', 'supervisor', 'complaint']
  },
  
  ai_provider: {
    provider: 'openai',
    apiKey: '',
    model: 'gpt-3.5-turbo',
    temperature: 0.7,
    maxTokens: 1000
  },
  
  validation_settings: {
    enabled: false,
    maxAttempts: 3,
    strictValidation: false,
    followUpStyle: 'friendly'
  },
  
  companyName: '',
  companyLogo: '',
  tcpaLanguage: 'By providing your phone number, you consent to receive text messages from our company. Message and data rates may apply. Reply STOP to opt out at any time.',
  
  custom_javascript: '',
  custom_html: '',
  
  operatorName: '',
  operatorAvatarUrl: ''
};

// Combined chatbot data interface
export interface UnifiedChatbotData {
  id: string;
  user_id: string;
  name: string;
  description: string | null;
  title: string;
  avatar_url: string | null;
  welcome_message: string;
  completion_message: string;
  active: boolean | null;
  created_at: string;
  updated_at: string;
  settings: UnifiedChatbotSettings;
  theme: ChatTheme | null;
  questions: Question[];
  triggerRules: TriggerRule[];
}

// Builder state interface
export interface BuilderState {
  // Data
  chatbot: UnifiedChatbotData | null;
  
  // Loading states
  isLoading: boolean;
  isSaving: boolean;
  
  // Error states
  error: string | null;
  
  // Dirty state tracking
  isDirty: boolean;
  dirtyFields: Set<string>;
  
  // UI state
  activeTab: string;
  unsavedChanges: boolean;
}

// Builder action types
export type BuilderAction =
  | { type: 'SET_LOADING'; payload: boolean }
  | { type: 'SET_SAVING'; payload: boolean }
  | { type: 'SET_ERROR'; payload: string | null }
  | { type: 'SET_CHATBOT'; payload: UnifiedChatbotData }
  | { type: 'UPDATE_SETTINGS'; payload: Partial<UnifiedChatbotSettings> }
  | { type: 'UPDATE_THEME'; payload: Partial<ChatTheme> }
  | { type: 'UPDATE_QUESTIONS'; payload: Question[] }
  | { type: 'UPDATE_TRIGGER_RULES'; payload: TriggerRule[] }
  | { type: 'UPDATE_BASIC_INFO'; payload: { name?: string; description?: string; title?: string; welcome_message?: string; completion_message?: string } }
  | { type: 'SET_DIRTY'; payload: { field: string; isDirty: boolean } }
  | { type: 'RESET_DIRTY' }
  | { type: 'SET_ACTIVE_TAB'; payload: string };
